import { createFileRoute, Link, notFound, redirect, isRedirect, useLoaderData, useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { formatEventDateRange } from "@/lib/event-date";
import { isImportedEventId, importedEventSlug, districtHubPath, hasDistrictHubTag } from "@/lib/item-link";
import { hasTfPendingTag } from "@/lib/tf-pending";

import type { SearchSchemaInput } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { Heart, MapPin, Search, Pencil, ArrowLeft, CalendarClock, Info as InfoIcon, ListChecks, Ticket, Clock, MessageSquare } from "lucide-react";
import { StarIcon } from "@/components/icons/StarIcon";

import { type Item } from "@/data/places";
import { usePublicPlaces } from "@/hooks/use-public-places";
import { supabase } from "@/integrations/supabase/client";
import { useFavorites } from "@/hooks/useFavorites";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAutoTranslate } from "@/i18n/useAutoTranslate";
import { useCity } from "@/context/CityContext";
import heroBanner from "@/assets/hero-helsinki.webp";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CountdownTicker } from "@/components/EventCountdown";
import { SmartImage } from "@/components/ui/SmartImage";
import { PlaceGallery } from "@/components/PlaceGallery";
import { PlaceReviews } from "@/components/PlaceReviews";
import { useReviewStat } from "@/hooks/use-review-stats";
import { BookingDialog } from "@/components/BookingDialog";
import { partnerHref } from "@/lib/partner-link";
import { buildHreflangLinks, buildLocaleMeta } from "@/lib/i18n-paths";
import { fetchEntityTranslations, fetchManyEntityTranslations, langFromPathname, tr } from "@/lib/i18n-content";
import { usePublicSettings } from "@/hooks/use-public-settings";
import { MiniMapPreview } from "@/components/MiniMapPreview";
import PlaceLocationsMap, { type PlaceLocationPin } from "@/components/PlaceLocationsMap";
import { ClientOnly } from "@tanstack/react-router";
import { MobileMapSheet } from "@/components/place/MobileMapSheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { DistrictLinkBanner } from "@/components/districts/DistrictLinkBanner";
import { applyDishTranslations, buildMenuJsonLd, placeMenuFromRow, primaryMenuUrl, EMPTY_PLACE_MENU, menuDishFromRow } from "@/lib/place-menu";
import { docsToPlaceMenu, loadPlaceMenuDocs } from "@/lib/place-menus";

import { PlaceRoute, PlaceNotFound, PlaceError } from "@/components/place/PlaceRouteView";
export const TRAIN_FACTORY_HUB_IDS = new Set(["train-factory-hub", "7d1405ee-748c-456f-82e4-cc868eef414d"]);
export const placeLoader = async ({ params, deps, location }: any) => {

    // Try DB by slug first (works for DB-managed places); fall back to static
    let item: Item | undefined;
    let curatedRelatedSlugs: string[] = [];

    let reviewStat = { count: 0, avg: 0 };
    try {
      let q = supabase.from("places").select("*").eq("slug", params.id);
      if (!deps?.preview) q = q.eq("published", true);
      const { data: loaded } = await q.maybeSingle();
      const data =
        loaded && !deps?.preview && hasTfPendingTag((loaded as any).tags) ? null : loaded;

      if (data && !deps?.preview) {
        const hubTo = districtHubPath((data as any).slug) || (hasDistrictHubTag((data as any).tags) ? "/train-factory" : null);
        if (hubTo) throw redirect({ to: hubTo });
      }

      if (data && (data as any).listing_type === "menu_only" && !deps?.preview) {
        // Menu-only listings live entirely on their menu page.
        throw redirect({ to: "/menu/$slug", params: { slug: (data as any).slug } });
      }

      if (data) {
        item = {
          dbId: (data as any).id,
          id: (data as any).slug || (data as any).id,
          section: (data as any).section,
          city: (data as any).city_slug,
          name: (data as any).name,
          category: (data as any).category ?? "",
          area: (data as any).area ?? "",
          address: (data as any).address ?? undefined,
          description: (data as any).description ?? "",
          image: (data as any).image_url ?? "",
          rating: (data as any).rating ?? undefined,
          tags: ((data as any).tags ?? []) as any,
          link: (data as any).link ?? undefined,
          reserveLink: (data as any).reserve_link ?? undefined,
          cta: (data as any).cta ?? undefined,
          date: formatEventDateRange((data as any).starts_at, (data as any).ends_at) ?? (data as any).date_label ?? undefined,
          startsAt: (data as any).starts_at ?? undefined,
          endsAt: (data as any).ends_at ?? undefined,
          price: (data as any).price ?? undefined,
          lat: (data as any).lat ?? undefined,
          lng: (data as any).lng ?? undefined,
          gallery: ((data as any).gallery_urls ?? []) as string[],
          overviewBody: (data as any).overview_body ?? "",
          detailsBody: (data as any).details_body ?? "",
          ticketsInfo: (data as any).tickets_info ?? "",
          scheduleInfo: (data as any).schedule_info ?? "",
          showCountdown: (data as any).show_countdown ?? true,
          eventStatusOverride: ((data as any).event_status_override ?? null) as any,
          buyTicketUrl: (data as any).buy_ticket_url ?? undefined,
          updatedAt: (data as any).updated_at ?? undefined,
          createdAt: (data as any).created_at ?? undefined,
          instagram: (data as any).instagram ?? undefined,
          facebook: (data as any).facebook ?? undefined,
          phone: (data as any).phone ?? undefined,
          email: (data as any).email ?? undefined,
          openingHours: ((data as any).opening_hours ?? null) as any,
          region: ((data as any).region ?? null) as any,
          // Menu documents travel with the loader so they render server-side.
          ...(await (async () => {
            const menuDocs = await loadPlaceMenuDocs(supabase, data as any);
            return {
              menuDocs,
              menu: menuDocs.length ? docsToPlaceMenu(menuDocs) : placeMenuFromRow(data as any),
            };
          })()),
          menuDishes: await (async () => {
            const { data: rows } = await supabase
              .from("place_menu_items")
              .select("*")
              .eq("place_id", (data as any).id)
              .eq("is_active", true)
              .order("sort_order", { ascending: true });
            return ((rows ?? []) as any[]).map((r) => menuDishFromRow(r));
          })(),
          hoursSource: {
            opening_hours: (data as any).opening_hours ?? null,
            kitchen_hours: (data as any).kitchen_hours ?? null,
            lunch_hours: (data as any).lunch_hours ?? null,
            special_hours: (data as any).special_hours ?? null,
            temporarily_closed: (data as any).temporarily_closed ?? null,
            temporarily_closed_note: (data as any).temporarily_closed_note ?? null,
          },
        } as Item & { updatedAt?: string; createdAt?: string; instagram?: string; facebook?: string; phone?: string; email?: string; openingHours?: Record<string, { closed?: boolean; open?: string; close?: string }> | null; region?: string | null };

        curatedRelatedSlugs = Array.isArray((data as any).related_place_slugs)
          ? ((data as any).related_place_slugs as string[]).filter(Boolean)
          : [];

        // Finnish URL tree (/fi/place/...) — apply stored translations here in
        // the loader so the Finnish copy is inside the server-rendered HTML
        // (indexable) instead of swapping in after hydration.
        const pageLang = langFromPathname(location?.pathname);
        if (pageLang === "fi") {
          // Menu dishes are translated too, so the Finnish page never mixes
          // Finnish copy with an English dish list.
          const dishes = ((item as any).menuDishes ?? []) as any[];
          if (dishes.length > 0) {
            const dishMap = await fetchManyEntityTranslations(
              "menu_item",
              dishes.map((d) => d.id),
              "fi",
            );
            (item as any).menuDishes = applyDishTranslations(dishes as any, dishMap);
          }
          const t = await fetchEntityTranslations("place", (data as any).id, "fi");
          if (Object.keys(t).length > 0) {
            item.name = tr(t, "name", item.name);
            item.description = tr(t, "description", item.description);
            item.area = tr(t, "area", item.area);
            (item as any).overviewBody = tr(t, "overview_body", (item as any).overviewBody);
            (item as any).detailsBody = tr(t, "details_body", (item as any).detailsBody);
            (item as any).scheduleInfo = tr(t, "schedule_info", (item as any).scheduleInfo);
            (item as any).ticketsInfo = tr(t, "tickets_info", (item as any).ticketsInfo);
          }
        }
      }

    } catch (err) {
      // Redirects (menu-only listings) must not be swallowed by this guard.
      if (isRedirect(err)) throw err;
      /* ignore — surface notFound below if DB miss */
    }
    if (!item) throw notFound();


    try {
      const { data: reviewRows, error: reviewError } = await supabase
        .from("place_reviews_public" as any)
        .select("rating")
        .eq("place_slug", item.id);
      if (!reviewError) {
        const rows = (reviewRows ?? []) as unknown as Array<{ rating: number }>;
        const count = rows.length;
        const avg = count ? rows.reduce((sum, row) => sum + row.rating, 0) / count : 0;
        reviewStat = { count, avg };
      }
    } catch {
      /* ignore review stat fetch failures */
    }

    // Load related places from DB. Prefer admin-curated picks, then same
    // section, then top up from the same city — admin updates flow through.
    let related: Item[] = [];
    try {
      const toItem = (d: any): Item => ({
        id: d.slug || d.id,
        section: d.section,
        city: d.city_slug,
        name: d.name,
        category: d.category ?? "",
        area: d.area ?? "",
        description: "",
        image: d.image_url ?? "",
        tags: (d.tags ?? []) as any,
      });

      // 1) Admin-curated picks from this place's related_place_slugs (preserve order).
      const curatedSlugs: string[] = curatedRelatedSlugs;

      if (curatedSlugs.length > 0) {
        const { data: curated } = await supabase
          .from("places")
          .select("slug, id, section, city_slug, name, category, area, image_url, tags")
          .eq("published", true)
          .in("slug", curatedSlugs);
        const bySlug = new Map<string, Item>();
        for (const row of (curated ?? []) as any[]) bySlug.set(row.slug, toItem(row));
        for (const slug of curatedSlugs) {
          const it = bySlug.get(slug);
          if (it && it.id !== item!.id) related.push(it);
        }
      }

      // 2) Top up with same-section picks if curator list is short.
      if (related.length < 3) {
        const { data: sameSection } = await supabase
          .from("places")
          .select("slug, id, section, city_slug, name, category, area, image_url, tags, updated_at")
          .eq("published", true)
          .eq("city_slug", item!.city)
          .eq("section", item!.section)
          .neq("slug", item!.id)
          .order("updated_at", { ascending: false })
          .limit(6);
        const seen = new Set(related.map((r) => r.id));
        for (const row of (sameSection ?? []) as any[]) {
          const it = toItem(row);
          if (!seen.has(it.id)) { related.push(it); seen.add(it.id); }
          if (related.length >= 3) break;
        }
      }

      // 3) Top up from the same city.
      if (related.length < 3) {
        const { data: sameCity } = await supabase
          .from("places")
          .select("slug, id, section, city_slug, name, category, area, image_url, tags, updated_at")
          .eq("published", true)
          .eq("city_slug", item!.city)
          .neq("slug", item!.id)
          .order("updated_at", { ascending: false })
          .limit(6);
        const seen = new Set(related.map((r) => r.id));
        for (const row of (sameCity ?? []) as any[]) {
          const it = toItem(row);
          if (!seen.has(it.id)) { related.push(it); seen.add(it.id); }
          if (related.length >= 3) break;
        }
      }
      related = related.slice(0, 3);
    } catch { /* ignore */ }

    // Load additional physical locations for this place (multi-branch).
    // One master profile, many pins — shown on the detail-page map and list.
    let locations: Array<{
      id: string; label: string | null; address: string | null; city_slug: string | null;
      postal_code: string | null; region: string | null; lat: number | null; lng: number | null;
      phone: string | null; email: string | null;
      opening_hours: Record<string, { open?: string | null; close?: string | null; closed?: boolean }> | null;
      is_primary: boolean; sort_order: number;
    }> = [];
    try {
      // Resolve internal place id (loader row above only kept the slug in `item.id`).
      const { data: placeRow } = await supabase.from("places").select("id").eq("slug", params.id).maybeSingle();
      const internalId = (placeRow as any)?.id as string | undefined;
      if (internalId) {
        const { data: locRows } = await supabase
          .from("place_locations" as any)
          .select("id,label,address,city_slug,postal_code,region,lat,lng,phone,email,opening_hours,is_primary,sort_order")
          .eq("place_id", internalId)
          .order("is_primary", { ascending: false })
          .order("sort_order", { ascending: true });
        locations = ((locRows ?? []) as any[]).map((r) => ({
          id: r.id,
          label: r.label ?? null,
          address: r.address ?? null,
          city_slug: r.city_slug ?? null,
          postal_code: r.postal_code ?? null,
          region: r.region ?? null,
          lat: r.lat != null ? Number(r.lat) : null,
          lng: r.lng != null ? Number(r.lng) : null,
          phone: r.phone ?? null,
          email: r.email ?? null,
          opening_hours: r.opening_hours ?? null,
          is_primary: !!r.is_primary,
          sort_order: r.sort_order ?? 0,
        }));
        // Best-effort geocode: fill missing lat/lng from the address so every
        // branch shows a real pin on the detail-page map (Nominatim is free
        // and CORS-friendly; failures are silent).
        const needsGeocode = locations.filter((l) => (l.lat == null || l.lng == null) && l.address);
        if (needsGeocode.length > 0) {
          await Promise.all(
            needsGeocode.map(async (l) => {
              try {
                const q = [l.address, l.city_slug, "Finland"].filter(Boolean).join(", ");
                const res = await fetch(
                  `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`,
                  { headers: { "Accept": "application/json" } },
                );
                if (!res.ok) return;
                const arr = (await res.json()) as Array<{ lat: string; lon: string }>;
                const hit = arr?.[0];
                if (hit) { l.lat = Number(hit.lat); l.lng = Number(hit.lon); }
              } catch { /* ignore */ }
            }),
          );
        }
      }
    } catch { /* ignore — table may be empty for older places */ }

    return { item, related, reviewStat, locations };
};

export const Route = createFileRoute("/place/$id")({
  validateSearch: (s: Record<string, unknown> & SearchSchemaInput): { preview?: number; loc?: string } => ({
    preview: s.preview === "1" || s.preview === 1 ? 1 : undefined,
    loc: typeof s.loc === "string" && s.loc.length > 0 && s.loc.length <= 64 ? s.loc : undefined,
  }),
  beforeLoad: ({ params }) => {
    // Imported/aggregated events live at /happening/<slug>; older links and
    // cards may still point at /place/imported-<slug>.
    if (isImportedEventId(params.id)) {
      throw redirect({
        to: "/happening/$slug",
        params: { slug: importedEventSlug(params.id) },
      });
    }
    // The Train Factory hub card opens the full district page.
    if (TRAIN_FACTORY_HUB_IDS.has(params.id)) throw redirect({ to: "/train-factory" });
  },
  loaderDeps: ({ search }) => ({ preview: search.preview === 1 }),
  loader: placeLoader,

  head: ({ params, loaderData }) => {
    const item = loaderData?.item;
    const reviewStat = loaderData?.reviewStat;
    if (!item) return { meta: [{ title: "Place — what" }] };

    const url = `https://whatfinland.com/place/${params.id}`;
    const ogType = item.section === "happening" ? "event" : "article";

    // Schema.org type per section
    const anyItem = item as any;
    const baseLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@id": url,
      name: item.name,
      description: item.description,
      image: item.image,
      url,
      ...(item.address && {
        address: { "@type": "PostalAddress", streetAddress: item.address, addressLocality: item.area, addressCountry: "FI" },
      }),
      ...(item.lat != null && item.lng != null && {
        geo: { "@type": "GeoCoordinates", latitude: item.lat, longitude: item.lng },
      }),
      ...(item.link && { sameAs: [item.link] }),
      ...(anyItem.updatedAt && { dateModified: anyItem.updatedAt }),
      ...(anyItem.createdAt && { datePublished: anyItem.createdAt }),
      ...(reviewStat && reviewStat.count > 0 && {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: Number(reviewStat.avg.toFixed(1)),
          reviewCount: reviewStat.count,
          bestRating: 5,
        },
      }),
    };

    // Schema.org Menu, generated from the same menu data the page renders.
    const menu = (anyItem.menu ?? EMPTY_PLACE_MENU) as ReturnType<typeof placeMenuFromRow>;
    const menuLd = buildMenuJsonLd({ menu, placeName: item.name, placeUrl: url, dishes: (anyItem.menuDishes ?? []) as any });
    const menuUrl = primaryMenuUrl(menu);

    let ld: Record<string, unknown>;
    if (item.section === "eat") {
      ld = {
        ...baseLd,
        "@type": "Restaurant",
        servesCuisine: item.category,
        priceRange: item.price ?? "€€",
        ...(menuLd || menuUrl ? { hasMenu: menuLd ?? menuUrl, ...(menuUrl ? { menu: menuUrl } : {}) } : {}),
      };
    } else if (item.section === "do") {
      ld = { ...baseLd, "@type": "TouristAttraction", touristType: item.category };
    } else {
      ld = {
        ...baseLd,
        "@type": "Event",
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        ...(item.date && { startDate: item.date }),
        location: {
          "@type": "Place",
          name: item.area,
          address: { "@type": "PostalAddress", streetAddress: item.address ?? item.area, addressLocality: item.area, addressCountry: "FI" },
        },
        ...(item.link && { offers: { "@type": "Offer", url: item.link, price: item.price ?? "0", priceCurrency: "EUR", availability: "https://schema.org/InStock" } }),
      };
    }

    const breadcrumbs = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://whatfinland.com/" },
        { "@type": "ListItem", position: 2, name: item.section === "eat" ? "Eat" : item.section === "do" ? "Do" : "Happening", item: `https://whatfinland.com/${item.section}` },
        { "@type": "ListItem", position: 3, name: item.name, item: url },
      ],
    };

    return {
      meta: [
        { title: `${item.name} — what · ${item.area}` },
        { name: "description", content: item.description },
        { property: "og:title", content: item.name },
        { property: "og:description", content: item.description },
        { property: "og:image", content: item.image },
        { property: "og:url", content: url },
        ...buildLocaleMeta("en"),
        { property: "og:type", content: ogType },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: item.image },
      ],
      links: [{ rel: "canonical", href: url }, ...buildHreflangLinks(`/place/${params.id}`)],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(ld) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
      ],
    };
  },
  notFoundComponent: () => <PlaceNotFound />,
  errorComponent: ({ error, reset }) => <PlaceError error={error as Error} reset={reset} />,
  component: PlaceRoute,
});

